import { useState } from 'react'
import { useEconomyStore } from '@/state/useEconomyStore'
import { craftItem } from '@/economy/api'
import { SMITHY_RECIPES, getRecipe } from '@/economy/recipes'
import { ITEM_DISPLAY_NAMES } from '@/economy/types'
import type { WalletInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

export function SmithyPanel({ wallet }: PanelProps) {
  const [selectedRecipe, setSelectedRecipe] = useState<ItemType | ''>('')
  const [multiplier, setMultiplier] = useState(1)
  const tradeLoading = useEconomyStore((s) => s.tradeLoading)
  const tradeError = useEconomyStore((s) => s.tradeError)
  const setTradeLoading = useEconomyStore((s) => s.setTradeLoading)
  const setTradeError = useEconomyStore((s) => s.setTradeError)
  const setWallet = useEconomyStore((s) => s.setWallet)

  const recipe = selectedRecipe ? getRecipe(selectedRecipe) : null
  const cargoMap = new Map(wallet?.cargo ?? [])

  function canCraft(): boolean {
    if (!recipe) return false
    return recipe.inputs.every(([item, qty]) => (cargoMap.get(item) || 0) >= qty * multiplier)
  }

  async function handleCraft() {
    if (!selectedRecipe) return
    setTradeLoading(true)
    setTradeError(null)
    try {
      await craftItem(selectedRecipe, multiplier)
      // Refresh wallet after craft
      const { getWallet } = await import('@/economy/api')
      const w = await getWallet()
      setWallet(w)
    } catch (e: any) {
      setTradeError(e.message || 'Crafting failed')
    }
    setTradeLoading(false)
  }

  return (
    <>
      {tradeError && <div className="panel-error">{tradeError}</div>}

      <div className="panel-section">
        <h3 className="panel-section__title">Available Recipes</h3>
        {SMITHY_RECIPES.map((item) => {
          const r = getRecipe(item)
          if (!r) return null
          return (
            <div
              key={item}
              className="panel-row"
              style={{ cursor: 'pointer', background: selectedRecipe === item ? 'rgba(75, 65, 50, 0.3)' : undefined }}
              onClick={() => { setSelectedRecipe(item); setMultiplier(1); setTradeError(null) }}
            >
              <span className="panel-row__label" style={{ fontWeight: selectedRecipe === item ? 700 : 400 }}>
                {ITEM_DISPLAY_NAMES[item]}
              </span>
              <span className="panel-row__value" style={{ fontSize: 11 }}>
                {r.inputs.map(([i, q]) => `${q} ${ITEM_DISPLAY_NAMES[i]}`).join(' + ')}
                {' → '}
                {r.outputs.map(([i, q]) => `${q} ${ITEM_DISPLAY_NAMES[i]}`).join(' + ')}
              </span>
            </div>
          )
        })}
      </div>

      {recipe && selectedRecipe && (
        <div className="panel-section">
          <h3 className="panel-section__title">Craft {ITEM_DISPLAY_NAMES[selectedRecipe]}</h3>

          <div className="panel-input-row">
            <span style={{ color: '#8b7355', fontSize: 12 }}>Multiplier:</span>
            {[1, 2, 5].map((m) => (
              <button
                key={m}
                className={`panel-btn ${multiplier === m ? 'panel-btn--primary' : ''}`}
                onClick={() => setMultiplier(m)}
              >
                {m}×
              </button>
            ))}
          </div>

          <div className="panel-section" style={{ background: '#1f1d19', padding: 10, borderRadius: 4 }}>
            <div style={{ color: '#8b7355', fontSize: 12, marginBottom: 6 }}>Requires:</div>
            {recipe.inputs.map(([item, qty]) => {
              const have = cargoMap.get(item) || 0
              const need = qty * multiplier
              return (
                <div key={item} className="panel-row">
                  <span className="panel-row__label">{ITEM_DISPLAY_NAMES[item]}</span>
                  <span className="panel-row__value" style={{ color: have >= need ? '#6b8b4a' : '#c45a5a' }}>
                    {have}/{need}
                  </span>
                </div>
              )
            })}
            <div style={{ color: '#8b7355', fontSize: 12, marginTop: 8, marginBottom: 4 }}>Produces:</div>
            {recipe.outputs.map(([item, qty]) => (
              <div key={item} className="panel-row">
                <span className="panel-row__label">{ITEM_DISPLAY_NAMES[item]}</span>
                <span className="panel-row__value">{qty * multiplier}</span>
              </div>
            ))}
          </div>

          <button
            className="panel-btn panel-btn--primary"
            style={{ width: '100%', marginTop: 12 }}
            disabled={tradeLoading || !canCraft()}
            onClick={handleCraft}
          >
            {tradeLoading ? 'Crafting...' : `Forge ${multiplier}× ${ITEM_DISPLAY_NAMES[selectedRecipe]}`}
          </button>
        </div>
      )}
    </>
  )
}
